import React, { useEffect } from 'react';
import { View, ScrollView, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../../components/common/Text';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import AppToolbar from '../../components/shared/AppToolbar';

const TermsOfServiceScreen = () => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();

  // Set meta description and Open Graph tags for web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        'ColourSense Terms of Service - Read our terms and conditions for using our colour analysis and style consultation services.'
      );

      // Update Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', 'ColourSense | Terms of Service');
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]'
      );
      if (ogDescription) {
        ogDescription.setAttribute(
          'content',
          'ColourSense Terms of Service - Read our terms and conditions for using our colour analysis and style consultation services.'
        );
      }
    }
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <AppToolbar
        showBackButton={false}
        title="Terms of Service"
        showProfile={false}
        showSettings={false}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.contentContainer,
          { paddingBottom: insets.bottom + 20 },
        ]}
      >
        <View style={[styles.content, isMobile && styles.contentMobile]}>
          <View style={styles.section}>
            <Text
              style={[
                styles.title,
                { color: colors.text, marginTop: 8, marginBottom: 16 },
              ]}
            >
              ColourSense Terms and Conditions
            </Text>
            <Text
              style={[
                styles.lastUpdated,
                {
                  color: colors.secondary,
                  textAlign: 'left',
                  marginBottom: 16,
                },
              ]}
            >
              Last Updated: November 25, 2025
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 16 },
              ]}
            >
              Welcome to ColourSense (&quot;we,&quot; &quot;us,&quot;
              &quot;our&quot;). These Terms and Conditions (&quot;Terms&quot;)
              govern your access to and use of our website, mobile application,
              and related services (collectively, the &quot;Services&quot;). By
              accessing or using our Services, you agree to be legally bound by
              these Terms. If you do not agree, please do not use the Services.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              1. Eligibility and Acceptance
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              1.1 Eligibility
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              By using the Services, you represent and warrant that you are at
              least 13 years old (or the minimum age required by law in your
              jurisdiction), have full legal capacity to enter into these Terms,
              and agree to comply with all applicable laws and regulations.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              1.2 Acceptance
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Your access to and use of the Services constitutes your acceptance
              of these Terms and any future amendments. If you do not agree,
              please do not use the Services.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              2. User Account Registration and Security
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              2.1 Account Creation
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Certain features require registration. You agree to provide true,
              accurate, current, and complete information about yourself during
              registration and maintain the accuracy of this information.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              2.2 Account Security
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You are responsible for safeguarding your account credentials and
              any activities or actions under your account. You agree to notify
              us immediately of any unauthorized use or suspected breach.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              2.3 Account Suspension or Termination
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We reserve the right to suspend or terminate your account if we
              suspect fraudulent or unauthorized activity, violation of these
              Terms, or any conduct detrimental to our Services.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              2.4 Multiple Accounts
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Unless expressly permitted, users may not maintain more than one
              account.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              3. User Content and Conduct
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              3.1 Ownership
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You retain all rights to content you upload or post (&quot;User
              Content&quot;). You grant us a non-exclusive, worldwide,
              royalty-free license to use, modify, reproduce, display,
              distribute, and promote your User Content within the Services and
              related marketing.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              3.2 User Content Standards
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You agree not to post content that:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Is unlawful, harmful, threatening, abusive, harassing,
              defamatory, vulgar, obscene, libelous, invasive of another&apos;s
              privacy, hateful, or racially, ethnically, or otherwise
              objectionable.{'\n'}• Infringes any intellectual property rights,
              privacy rights, or other rights of third parties.{'\n'}• Contains
              viruses, malware, or other harmful code.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              3.3 Monitoring and Enforcement
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We do not generally monitor User Content but reserve the right to
              remove or disable access to any content violating these Terms. We
              may cooperate with law enforcement or third parties regarding
              claims related to User Content.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              3.4 User Responsibility
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You are solely responsible for your User Content and the
              consequences of posting it. You agree to indemnify and hold
              ColourSense harmless from claims arising from your User Content.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              3.5 Community Conduct
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You agree to use the Services respectfully and not to interfere
              with the experience of other users. Harassment, spamming,
              impersonation, and disruptive behavior are prohibited.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              4. Service Use and Restrictions
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              4.1 Acceptable Use
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You agree not to use the Services for any unlawful or prohibited
              activities including, but not limited to:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Violating any applicable laws or regulations.{'\n'}• Engaging in
              fraudulent, deceptive, or harmful activities.{'\n'}• Attempting to
              gain unauthorized access to any systems or user accounts.{'\n'}•
              Using automated systems, bots, or scrapers without our permission.
              {'\n'}• Transmitting harmful code, viruses, or malicious software.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              4.2 Third-Party Links
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Our Services may contain links to third-party websites or
              services. We do not endorse or assume responsibility for any
              third-party content, products, or services.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              4.3 Service Availability
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We strive to provide uninterrupted access but do not guarantee
              availability or error-free performance. We reserve the right to
              modify, suspend, or discontinue features or Services at any time.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              5. Subscriptions, Payments, and Billing
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              5.1 Subscription Plans
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Some Services require payment of subscription fees. By
              subscribing, you agree to pay the fees and taxes associated with
              your plan.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              5.2 Billing Cycle and Payment Methods
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Subscriptions are billed in advance on a monthly or annual basis,
              depending on your plan. Payments are processed securely through
              third-party providers such as Stripe or PayPal.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              5.3 Auto-Renewal and Cancellation
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Subscriptions automatically renew unless canceled at least 24
              hours before the end of the current billing period. You can cancel
              via account settings.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              5.4 Refunds
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              All payments for subscriptions and services are generally
              non-refundable, including any fees for partially used subscription
              periods. However, we may consider refund requests made within
              fourteen (14) days from the date of purchase, subject to our
              discretion and compliance with applicable consumer protection laws
              in your jurisdiction.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              Please note that purchases of virtual items, digital content, or
              any non-tangible goods are final and cannot be refunded.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              5.5 Payment Disputes
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You agree to resolve any payment disputes directly with us before
              initiating chargebacks or other payment reversals.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              6. Intellectual Property Rights
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              6.1 Our Rights
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              All content, trademarks, logos, software, and materials on the
              Services are owned or licensed by ColourSense and protected by
              applicable intellectual property laws.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              6.2 Limited License
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We grant you a limited, revocable, non-exclusive license to access
              and use the Services for personal, non-commercial purposes,
              subject to these Terms.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              6.3 Prohibited Use
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You may not:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Copy, modify, distribute, sell, or lease any part of the
              Services.{'\n'}• Reverse engineer or attempt to extract source
              code or underlying ideas.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              7. Privacy and Data Protection
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Your privacy is important to us. Please see our Privacy Policy for
              details on how we collect, use, and protect your personal
              information.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              8. Disclaimers and Limitation of Liability
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              8.1 No Warranty
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              The Services are provided &quot;as is&quot; without warranties of
              any kind, either express or implied, including merchantability or
              fitness for a particular purpose.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              8.2 No Guarantee
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We do not warrant that the Services will be error-free or
              uninterrupted.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              8.3 Limitation of Liability
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              To the fullest extent permitted by law, ColourSense and its
              affiliates are not liable for any indirect, incidental,
              consequential, or punitive damages arising from your use of the
              Services.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              8.4 Indemnification
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You agree to indemnify and hold harmless ColourSense and its
              affiliates against any claims arising from your breach of these
              Terms.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              9. Termination and Suspension
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              9.1 Termination by You
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You may terminate your account at any time via your account
              settings. Termination does not relieve you of any obligations
              incurred prior to termination.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              9.2 Termination by Us
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We may suspend or terminate your account or access for violations
              of these Terms or any applicable law.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              9.3 Effect of Termination
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Upon termination, your right to access the Services ceases. We may
              retain your data as required by law or for legitimate business
              purposes.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              10. Governing Law and Dispute Resolution
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              10.1 Governing Law
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              These Terms are governed by and shall be construed in accordance
              with the laws of the Republic of the Philippines, without regard
              to its conflict of law principles.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              10.2 Dispute Resolution
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Any dispute, controversy, or claim arising out of or relating to
              these Terms, or the breach, termination, or invalidity thereof,
              shall be submitted exclusively to the courts of the Philippines,
              and you hereby consent to the jurisdiction of such courts.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              11. Changes to Terms
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We may update these Terms periodically. Significant changes will
              be communicated through email or in-app notifications. Continued
              use after changes means you accept the new Terms.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              12. Miscellaneous
            </Text>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              12.1 Entire Agreement
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              These Terms, along with the Privacy Policy and any additional
              policies, constitute the entire agreement between you and
              ColourSense.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              12.2 Severability
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              If any provision of these Terms is found invalid or unenforceable,
              the remaining provisions shall remain in full force and effect.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              12.3 Waiver
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Failure to enforce any provision does not constitute a waiver of
              any rights.
            </Text>
            <Text
              style={[
                styles.subsectionTitle,
                { color: colors.text, marginTop: 12 },
              ]}
            >
              12.4 Force Majeure
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              ColourSense shall not be liable for failure or delay due to causes
              beyond our reasonable control, including natural disasters,
              technical failures, or acts of government.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              13. Contact Information
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              For questions, disputes, or legal notices, please contact us at:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              Email:{' '}
              <Text style={[styles.sectionText, { color: colors.primary }]}>
                support@mail.mycoloursense.com
              </Text>
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  content: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  contentMobile: {
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'left',
  },
  lastUpdated: {
    fontSize: 14,
    marginBottom: 32,
    textAlign: 'center',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 12,
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 8,
  },
  sectionText: {
    fontSize: 16,
    lineHeight: 24,
  },
});

export default TermsOfServiceScreen;
